import { forwardRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLocale } from './useLocale'

/**
 * Locale-prefix helpers. ~30 hand-built `/${locale}/…` strings caused three
 * separate regression classes (relative links missing the slash, locale-less
 * dashboard links, etc.) — build paths through these instead.
 */

/** Returns `(path) => /{locale}{path}`; called with no path gives the locale root. */
export function useLocalePath() {
  const locale = useLocale()
  return (path = '') => `/${locale}${path}`
}

/** <Link> that prefixes `to` with the active locale. */
export const LocaleLink = forwardRef<HTMLAnchorElement, Omit<React.ComponentProps<typeof Link>, 'to'> & { to: string }>(
  function LocaleLink({ to, ...props }, ref) {
    const localePath = useLocalePath()
    return <Link ref={ref} to={localePath(to)} {...props} />
  },
)

/**
 * Navigate that swaps the locale segment in place, preserving the rest of the
 * path and the query string — no full page reload, so SPA state survives.
 */
export function useLocaleNavigate() {
  const navigate = useNavigate()
  const location = useLocation()
  return (next: string) => {
    const parts = location.pathname.split('/')
    parts[1] = next
    navigate(`${parts.join('/')}${location.search}${location.hash}`)
  }
}